
import { useState, useEffect } from 'react';

// Define types for the data
export interface IncomeTreatment {
  id: number;
  patientName: string;
  service: string;
  date: string;
  amount: number;
  status: 'completed' | 'cancelled';
}

export interface IncomeData {
  totalIncome: number;
  totalServices: number;
  averageCaseValue: number;
  monthlyData: {
    month: string;
    income: number;
  }[];
  ytdTotal: number; // Added this property to match the required type
}

export const useIncomeSummary = () => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Sample data - in a real app, this would come from an API
  const treatmentData: IncomeTreatment[] = [
    {
      id: 1,
      patientName: 'Emily Johnson',
      service: 'Dental Implant',
      date: '2025-04-02',
      amount: 3500,
      status: 'completed'
    },
    {
      id: 2,
      patientName: 'Michael Chen',
      service: 'Root Canal',
      date: '2025-03-15',
      amount: 1200,
      status: 'completed'
    },
    {
      id: 3,
      patientName: 'Sarah Williams',
      service: 'Teeth Whitening',
      date: '2025-02-28',
      amount: 800,
      status: 'completed'
    },
    {
      id: 4,
      patientName: 'David Rodriguez',
      service: 'Crown',
      date: '2025-02-12',
      amount: 1100,
      status: 'completed'
    },
    {
      id: 5,
      patientName: 'Jennifer Lee',
      service: 'Dental Implant',
      date: '2025-01-25',
      amount: 3200,
      status: 'cancelled'
    },
    {
      id: 6,
      patientName: 'Robert Smith',
      service: 'Veneers (4 units)',
      date: '2025-01-18',
      amount: 2800,
      status: 'completed'
    },
    {
      id: 7,
      patientName: 'Anna Thompson',
      service: 'Braces Adjustment',
      date: '2024-12-30',
      amount: 350,
      status: 'completed'
    },
    {
      id: 8,
      patientName: 'Maria Garcia',
      service: 'Extraction',
      date: '2024-12-15',
      amount: 500,
      status: 'completed'
    },
    {
      id: 9,
      patientName: 'Alex Johnson',
      service: 'Full Mouth Reconstruction',
      date: '2024-11-28',
      amount: 8500,
      status: 'completed'
    },
    {
      id: 10,
      patientName: 'Lisa Wong',
      service: 'Root Canal',
      date: '2024-11-15',
      amount: 1200,
      status: 'cancelled'
    },
    {
      id: 11,
      patientName: 'Thomas Davis',
      service: 'Dental Implant',
      date: '2024-10-22',
      amount: 3300,
      status: 'completed'
    },
    {
      id: 12,
      patientName: 'Jessica Brown',
      service: 'Invisalign (Full)',
      date: '2024-10-08',
      amount: 5500,
      status: 'completed'
    },
    {
      id: 13,
      patientName: 'James Wilson',
      service: 'Wisdom Tooth Extraction',
      date: '2024-09-25',
      amount: 1800,
      status: 'completed'
    },
    {
      id: 14,
      patientName: 'Sophia Martinez',
      service: 'Bridge (3-unit)',
      date: '2024-09-12',
      amount: 2700,
      status: 'completed'
    },
    {
      id: 15,
      patientName: 'Daniel Taylor',
      service: 'Sleep Apnea Device',
      date: '2024-08-30',
      amount: 1900,
      status: 'completed'
    }
  ];

  // Calculate monthly data for the chart (last 12 months)
  const calculateMonthlyData = () => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const currentDate = new Date();
    const result = [];
    
    // Generate data for the past 12 months
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(currentDate);
      monthDate.setMonth(currentDate.getMonth() - i);
      
      const monthYear = monthDate.getMonth();
      const year = monthDate.getFullYear();
      
      // Filter treatments for this month and year and sum their amounts
      const monthlyIncome = treatmentData
        .filter(treatment => {
          const treatmentDate = new Date(treatment.date);
          return treatmentDate.getMonth() === monthYear && 
                 treatmentDate.getFullYear() === year &&
                 treatment.status === 'completed';
        })
        .reduce((sum, treatment) => sum + treatment.amount, 0);
      
      result.push({
        month: `${monthNames[monthYear]} ${year}`,
        income: monthlyIncome
      });
    }
    
    return result;
  };

  // Calculate total income, services, and average case value
  const calculateIncomeData = (): IncomeData => {
    const completedTreatments = treatmentData.filter(
      treatment => treatment.status === 'completed'
    );
    
    const totalIncome = completedTreatments.reduce(
      (sum, treatment) => sum + treatment.amount, 0
    );
    
    const totalServices = completedTreatments.length;
    
    const averageCaseValue = totalServices > 0 
      ? Math.round(totalIncome / totalServices) 
      : 0;
    
    // Calculate Year-to-Date total
    const currentYear = new Date().getFullYear();
    const ytdTotal = completedTreatments
      .filter(treatment => {
        const treatmentDate = new Date(treatment.date);
        return treatmentDate.getFullYear() === currentYear;
      })
      .reduce((sum, treatment) => sum + treatment.amount, 0);
    
    return {
      totalIncome,
      totalServices,
      averageCaseValue,
      monthlyData: calculateMonthlyData(),
      ytdTotal // Add the ytdTotal property to the returned object
    };
  };

  const incomeData = calculateIncomeData();

  useEffect(() => {
    // Simulate API loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  return {
    incomeData,
    treatmentData,
    isLoading
  };
};
